export const RECOVER_FUNNEL_GENERATION_ACTION = "recover_funnel_generation";
export const RETRY_SECTIONS_ACTION = "retry_sections";

export type FunnelRecoveryMode = "funnel" | "sections";

export interface FunnelGenerationRecoveryState {
  website_id: string;
  done: boolean;
  has_errors: boolean;
  error_sections: string[];
  generation_status: "recovering" | "failed";
  retry_available: boolean;
  recovery_action?: string | null;
}

export interface FunnelRecoveryDependencies {
  recoverFunnelGeneration: (websiteId: string) => Promise<unknown>;
  retrySection: (websiteId: string, sectionName: string) => Promise<unknown>;
}

export interface FunnelRecoveryResult {
  mode: FunnelRecoveryMode;
  succeeded: boolean;
  attemptedSections: number;
  failedSections: number;
}

export const resolveFunnelRecoveryMode = (
  recoveryAction: string | null | undefined,
  errorSections: string[]
): FunnelRecoveryMode => {
  if (recoveryAction === RECOVER_FUNNEL_GENERATION_ACTION) return "funnel";
  if (recoveryAction === RETRY_SECTIONS_ACTION) return "sections";

  // Compatibility with older backend responses that predate recovery_action.
  return errorSections.length === 0 ? "funnel" : "sections";
};

export const buildRecoveringFunnelState = (
  websiteId: string,
  recoveryAction?: string | null
): FunnelGenerationRecoveryState => ({
  website_id: websiteId,
  done: false,
  has_errors: false,
  error_sections: [],
  generation_status: "recovering",
  retry_available: false,
  recovery_action: recoveryAction,
});

export const buildFailedFunnelRecoveryState = (
  websiteId: string,
  errorSections: string[],
  recoveryAction?: string | null
): FunnelGenerationRecoveryState => ({
  website_id: websiteId,
  done: false,
  has_errors: true,
  error_sections: errorSections,
  generation_status: "failed",
  retry_available: true,
  recovery_action: recoveryAction,
});

export const executeFunnelRecovery = async (
  websiteId: string,
  errorSections: string[],
  recoveryAction: string | null | undefined,
  dependencies: FunnelRecoveryDependencies
): Promise<FunnelRecoveryResult> => {
  const mode = resolveFunnelRecoveryMode(recoveryAction, errorSections);

  if (mode === "funnel") {
    await dependencies.recoverFunnelGeneration(websiteId);
    return {
      mode,
      succeeded: true,
      attemptedSections: 0,
      failedSections: 0,
    };
  }

  const results = await Promise.allSettled(
    errorSections.map((sectionName) => dependencies.retrySection(websiteId, sectionName))
  );
  const failedSections = results.filter((result) => result.status === "rejected").length;

  return {
    mode,
    succeeded: results.length > 0 && failedSections !== results.length,
    attemptedSections: results.length,
    failedSections,
  };
};
